import { useTranslation } from "react-i18next";
import { useEffect, useRef } from "react";
import styled from "styled-components";

import {
    loginFormScenario,
    getCurrentUser,
    getSession,
} from "components/helper/callApi";
import { useSession } from "components/helper/sessionContext";
import { cleanWhitespace } from "components/helper/stringHelper";
import { useUser } from "components/helper/userContext";
import { useToken } from "components/helper/tokenContext";
import Form from "components/authentificationMode/common/Form";
import { useHandleErrors } from "components/authentificationMode/common/handleErrors";
import Spinner from "components/common/Spinner";

import {
    HTTPResponseError,
    NotFoundError,
    UnknownError,
} from "services/errors";

import { SessionType } from "types/sessionType";
import { FieldType, Scenario } from "types/scenarioType";
import { getAdditionalFields } from "services/additionalFields";
import { User } from "types/User";

type Props = {
    fields: FieldType[];
    codeOnline: string;
    scenario: Scenario;
    onError: (error: NotFoundError | UnknownError) => void;
    successMessage?: string | null;
    errorMessage?: string | null;
};

const BasicForm = ({
    fields,
    codeOnline,
    scenario,
    onError,
    successMessage,
    errorMessage,
}: Props) => {
    const userContext = useUser();
    const { t } = useTranslation();
    const { setSession } = useSession();
    const { setToken } = useToken();

    const { handleLoginError, handleCurrentUserError, handleSessionError } =
        useHandleErrors();

    const autoSubmitted = useRef(false);
    const isAnonym =
        scenario.verificationMethods.length === 0 && fields.length === 0;
    useEffect(() => {
        if (isAnonym && !autoSubmitted.current) {
            autoSubmitted.current = true;
            onSubmit();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isAnonym, autoSubmitted]);

    const onSubmit = async (data: any = {}) => {
        try {
            const dataCleaned = data;
            Object.keys(data).forEach(function (property) {
                dataCleaned[property] = cleanWhitespace(data[property]);
            });

            let token: string | null = null;
            try {
                token = await loginFormScenario(
                    codeOnline,
                    scenario,
                    dataCleaned,
                );
            } catch (error) {
                if (error instanceof HTTPResponseError) {
                    handleLoginError(error.response.status);
                } else {
                    handleLoginError(500);
                }
                return;
            }

            if (token) {
                setToken(token);
            }

            let userData: User | null = null;
            try {
                userData = await getCurrentUser(getAdditionalFields({}), token);
            } catch (error: any) {
                if (error instanceof HTTPResponseError) {
                    handleCurrentUserError(error.response.status);
                }
                handleCurrentUserError(error?.status ?? 500);
                return;
            }
            userContext.setUser(userData);

            let sessionData: SessionType | null = null;
            try {
                sessionData = await getSession(codeOnline, ["zoomSignature"]);
            } catch (error) {
                if (error instanceof HTTPResponseError) {
                    handleSessionError(error.response.status);
                }
                return;
            }

            setSession(sessionData);
        } catch (error) {
            onError(new UnknownError());
        }
    };

    return (
        <>
            {isAnonym ? (
                <SpinnerWrapper>
                    <Spinner />
                </SpinnerWrapper>
            ) : (
                <Form
                    onSubmit={onSubmit}
                    fields={fields}
                    successMessage={successMessage}
                    errorMessage={errorMessage}
                    formMessage={t(`enterPin.${window.env.REACT_APP_THEME}`)}
                    formButtonText={t("joinIn")}
                />
            )}
        </>
    );
};

const SpinnerWrapper = styled.div`
    padding-bottom: 20px;
`;

export default BasicForm;
